/*** IMPORTS FROM imports-loader ***/
var fetchPolyfill = require("polyfills/fetch");
var classListPolyfill = require("polyfills/classList");

/** @module components/lyst-app */
import _ from "underscore";
import baustein from "baustein";
import gql from "graphql-tag";
import analytics from "../analytics/analytics";
import requester from "modules/requester";
import facebookEvents from "../analytics/facebook-events";
import doubleclickEvents from "../analytics/doubleclick-events";
import environment from "modules/environment";
import Stack from "modules/stack";
import userProfiler from "modules/userprofiler";
import browser from "../utils/browser";
import events from "dom/events";
import globalEvents from "../utils/global-events";
import url from "../utils/url";
import Overlay from "../utils/overlay";
import alerts from "../utils/alerts";
import history from "../utils/history";
import FastClick from "../lib/fastclick/fastclick";
import globals from "../modules/globals";
import logging from "utils/logging";
import SignupOverlay from "components/overlays/signup-overlay";
import { events as calloutEvents } from "components/app-header-callout";
import { languageIsEnglish } from "modules/language";
import { gettext } from "modules/django-i18n";
import { client } from "react/lyst-apollo-client";

import crmSurveyTemplate from "../templates/surveys/crm-survey";

const LYST_APP_CALLOUT_SHOWN_MODIFIER = "lyst-app--callout-shown";
const LAYOUT_CONTAINER_SELECTOR = ".lyst-app__layout-container";
const LAYOUT_CONTAINER_COVER_IMAGE_MODIFIER = "lyst-app__layout-container--with-cover";
const LAYOUT_CONTAINER_ANIMATED_MODIFIER = "lyst-app__layout-container--animated";
const LAYOUT_CONTAINER_DIMMED_MODIFIER = "lyst-app__layout-container--dimmed";
const LAYOUT_MATTE_SELECTOR = ".lyst-app__matte";
const APP_HEADER_COMPONENT = "app-header";

/**
 * This component encapsulates the entire lyst web app. It handles page load set up, any
 * subsequent client side navigation, and ideally also will manage the lifecycle of overlays.
 * @constructor
 * @alias module:components/lyst-app
 * @extends module:baustein
 */
export default baustein.register(
    "lyst-app",
    /** @lends module:components/lyst-app.prototype */
    {
        defaultOptions: {
            surveyEnabled: false,
            exitOverlaySource: "",
            exitOverlayUrl: ""
        },

        overlays: [
            "base-overlay",
            "add-to-collection",
            "add-to-new-collection",
            "country-overlay",
            "editing-overlay",
            "email-capture-overlay",
            "product-overlay"
        ],

        membershipPages: ["sign_in", "new_signup", "signup", "signup form"],

        setupEvents(add) {
            add("feedFiltering", this.onFeedFilter);
            add("overlayOpen", this._onOverlayOpen);
            add("overlayCloseIntent", this._onOverlayCloseIntent);
            add("overlayClose", this._onOverlayClose);
            add("mobileNavOpen", this.blockScrolling);
            add("mobileNavClose", this.unblockScrolling);
            add("searchOpened", this.onSearchOpen);
            add("searchClosed", this.onSearchClose);
            add("addedToBag", this.openCartCallout);
            add("savedForLater", this.triggerSaveForLaterCallout);
            add("emailCaptureSuccess", "email-capture-overlay", this.onEmailCaptureSuccess);
            add("searchLayoutInserted", "search-layout", this.onSearchLayoutInserted);
            add("appHeaderUpdatedOffset", this.onHeaderUpdatedOffset);
            add("desktopHeaderUpdatedOffset", this.onHeaderUpdatedOffset);
            add("oosItemFound", "cart-summary", this.onCartCalloutOpened);
            add("closeJoinCallout", this.closeJoinCallout);
            add("saveForLaterButtonInserted", this._onSaveForLaterButtonInserted);
            add("cartSummaryShown", this.closeSaveForLaterCallout);
            add("triggerSignup", this.showSignUpOverlay);
            add("showCsaUnit", this.showCsaUnit);
            add("fixPageContent", this.fixPageContent);
            add("blockMobileScroll", this.blockMobileScroll);
            add("unblockMobileScroll", this.unblockMobileScroll);
            add("unFixPageContent", this.unFixPageContent);
            add("showInstantFeedback", this.showInstantFeedback);
            add("click", LAYOUT_MATTE_SELECTOR, this.onLayoutMatteClick);
            [
                "appHeaderMenuOpened",
                "appHeaderAutoCompleteOpened",
                calloutEvents.OPEN_LYST_MATTE
            ].forEach(e => add(e, this.addLayoutMatteAndBlockScroll));
            add(calloutEvents.OPEN_LYST_MATTE_WITH_SCROLL, this.addLayoutMatte);
            [
                "appHeaderMenuClosed",
                "appHeaderAutoCompleteClosed",
                calloutEvents.CLOSE_LYST_MATTE
            ].forEach(e => add(e, this.removeLayoutMatte));
        },

        init() {
            this.attachWatcher();
            this.crmSurveyOverlay;
            this.overlayStack = new Stack();
            this.sflProductIdsQueue = [];

            if (environment.get("facebookEvent")) {
                // we have a facebook page event to send
                this._trackFacebookEvent();
            }

            if (environment.get("doubleclickEvent")) {
                this._trackDoubleclickEvent();
            }
        },

        onInsert() {
            this._initFacebookPromoOverlay();
            this._initFastClick();
            this._addSupportClasses();

            // these are messages created using the Django messages framework
            // See https://docs.djangoproject.com/en/1.8/ref/contrib/messages/ for more info
            _.each(environment.get("messages"), alerts.alert);

            const parsedURL = url.parse(globals.window.location);

            // show instant notification if registered = "true" and area are set
            const notificationArea = parsedURL.searchParams.get("area");
            if (
                (
                    parsedURL.searchParams.get("loggedin") === "true" ||
                    parsedURL.searchParams.get("registered") === "true"
                ) && notificationArea
            ) {
                this.showInstantFeedback({ area: notificationArea });
            }

            // trigger the sign-up overlay if supplied certain query parameters
            if (
                parsedURL.searchParams.get("signup") === "true" && !userProfiler.isLoggedIn()
            ) {
                let signupOverlay = new SignupOverlay({
                    queryParams: url.toURLSearchParams({
                        signuptitle: parsedURL.searchParams.get("signuptitle")
                    })
                });
                signupOverlay.appendTo(this);
                signupOverlay.open();
            }

            // show Join Lyst callout for non-authenticated users
            if (userProfiler.shouldTriggerJoinCallout()) {
                this.triggerJoinCallout();
            }

            // survey ran by the CRM team
            const isCRMSurveyEnabled = environment.get("isCrmSurveyEnabled");
            const isHomepage = environment.get("pageType") === "homepage";
            const isFeeds = environment.get("pageType") === "feed";
            const isNotCustomLandingPage = environment.get("pageSubType") !== "custom_landing_page";
            const hasCrmSurveyShown = userProfiler.getCrmSurveyShown();
            if (
                isCRMSurveyEnabled &&
                (isHomepage || (isFeeds && isNotCustomLandingPage)) &&
                !hasCrmSurveyShown
            ) {
                this._openCRMSurvey();
            }

            // fire stock check on page load
            const header = this.findComponent(APP_HEADER_COMPONENT);
            if (header) {
                const cartButton = header.findComponent("app-header-cart");

                if (cartButton) {
                    cartButton.checkStock();
                }

                let stickyBuyButton = this.findComponent("buybuybuy-sticky-buy-button");

                if (stickyBuyButton) {
                    stickyBuyButton.setHeaderHeightReference(
                        header.el.getBoundingClientRect().height
                    );
                }
            }
        },

        onRemove() {
            this.releaseGlobalHandler("mouseleave", this.onBodyMouseLeave);
        },

        onFeedFilter() {
            this.findComponent("feed-main").showFilteringOverlay();
        },

        onEmailCaptureSuccess() {
            analytics.event("email_capture", "created", this.options.exitOverlaySource);
        },

        /**
         * Block the scrolling of the body.
         */
        blockScrolling() {
            this.el.classList.add("no-scroll");
        },

        /**
         * Un-block the scrolling of the body.
         */
        unblockScrolling() {
            this.el.classList.remove("no-scroll");
        },

        /**
         * On mobile safari setting overflow:hidden is not enough to not stop scrolling
         */
        blockMobileScroll() {
            if (browser.ios) {
                globals.document.addEventListener("touchmove", this.preventDefault, {
                    passive: false
                });
            }
        },
        /**
         * Release the scroll block on mobile safari
         */
        unblockMobileScroll() {
            if (browser.ios) {
                globals.document.removeEventListener("touchmove", this.preventDefault, {
                    passive: false
                });
            }
        },
        /**
         * A function to reference when we want to prevent a default browser behaviour
         * @param {Event} event
         */
        preventDefault(event) {
            event.preventDefault();
            return false;
        },
        fixPageContent() {
            this.el.classList.add("fix-element");
        },

        unFixPageContent() {
            this.el.classList.remove("fix-element");
        },

        _openCRMSurvey() {
            this.crmSurveyOverlay = new Overlay({
                cssClasses: ["size-m", "white"],
                content: crmSurveyTemplate({
                    title: gettext("looking_for_specific_item_survey.title"),
                    yes: gettext("looking_for_specific_item_survey.option.yes"),
                    no_rough_idea: gettext(
                        "looking_for_specific_item_survey.option.no_but_has_rough_idea"
                    ),
                    no_browsing: gettext(
                        "looking_for_specific_item_survey.option.no_is_just_browsing"
                    ),
                    submit: gettext("oos_product.inventory_request_form_submit_btn_text")
                })
            });
            globalEvents.on("crm-survey-closed", this._closeCRMSurvey.bind(this));
        },

        _closeCRMSurvey() {
            this.crmSurveyOverlay.close();
            globalEvents.off("crm-survey-closed", this._closeCRMSurvey);
        },

        /**
         * Tracks Facebook events.
         * @private
         */
        _trackFacebookEvent() {
            var event = environment.get("facebookEvent");
            analytics.tagEvent(analytics.TAG_NETWORKS.FACEBOOK, facebookEvents[event]);
        },

        /**
         * Tracks DoubleClick events.
         * @private
         */
        _trackDoubleclickEvent() {
            var event = environment.get("doubleclickEvent");
            analytics.tagEvent(
                analytics.TAG_NETWORKS.DOUBLECLICK,
                doubleclickEvents[event.name],
                event.data
            );
        },

        /**
         * Initialises the facebook promo overlay if required.
         * @returns {Overlay|Null}
         * @private
         */
        _initFacebookPromoOverlay() {
            var params = this._getParamsFromRequest();
            var overlay = null;

            if (
                params.get("utm_source") === "fb" &&
                params.get("utm_medium") === "cpc" &&
                params.get("overlay") === "true"
            ) {
                this.options.exitOverlayUrl = "";

                overlay = new Overlay({
                    url: url.unparse({
                        path: "/account/register/fb-promo/",
                        searchParams: params
                    }),
                    cssClasses: "size-l white fb-promo-overlay"
                });
            }

            return overlay;
        },

        _getParamsFromRequest() {
            var parsed = url.parse(globals.window.location.href);
            return parsed.searchParams;
        },

        /**
         * Initialises FastClick on this component. FastClick removes the 300ms
         * delay that is often added by devices with touch screens.
         * For more info see: https://github.com/ftlabs/fastclick
         * @private
         */
        _initFastClick() {
            // let FastClick decide whether or not to do anything
            return FastClick.attach(this.el);
        },

        /**
         * Adds classes to this component that indicate whether certain
         * features are supported or not.
         * @private
         */
        _addSupportClasses() {
            var classList = this.el.classList;

            // add class to the body to indicate whether this device has touch support
            classList.add(browser.hasTouch ? "has-touch" : "no-touch");

            // if device supports css transforms add class to body for CSS to hook into
            classList.toggle("has-css-transforms", browser.supportsCSSTransforms);

            // if device has css transitions
            classList.toggle("no-css-transitions", !browser.supportsCSSTransitions);

            // If scrollbars are stylable, add a class to help.
            classList.toggle("css-scrollbars", browser.supportsCSSScrollbars);

            // if we don't support new flexbox then add a class to help
            classList.toggle("no-flexbox", !browser.supportsFlexbox);
        },

        getLayoutContainer() {
            return this.el.querySelector(LAYOUT_CONTAINER_SELECTOR);
        },

        hideContent() {
            this.getLayoutContainer().classList.add("hidden");
            this.el.querySelector(".background-banner-wrapper").classList.add("hidden");
        },

        showContent() {
            this.getLayoutContainer().classList.remove("hidden");
            this.el.querySelector(".background-banner-wrapper").classList.remove("hidden");
        },

        openCartCallout() {
            // Increment cart button item count by 1
            globalEvents.trigger("cart.change", { itemCountDifference: 1 });

            const header = this.findComponent(APP_HEADER_COMPONENT);
            const cartButton = header.findComponent("app-header-cart");

            // show cart callout
            cartButton.showCartCallout();

            this.onCartCalloutOpened();
        },

        onCartCalloutOpened() {
            // show desktop header, add matte, prevent scrolling
            this.invoke(this.findComponent("desktop-header"), "show");
            this.addLayoutMatteAndBlockScroll();
            this.el.classList.add(LYST_APP_CALLOUT_SHOWN_MODIFIER);

            // close existing callouts
            this.closeJoinCallout();
            this.closeSaveForLaterCallout();
        },

        onHeaderUpdatedOffset(event) {
            const { offset } = event;
            const stickyBuyButton = this.findComponent("buybuybuy-sticky-buy-button");

            if (stickyBuyButton) {
                stickyBuyButton.updatePosition(offset);
            }

            if (event.type === "appHeaderUpdatedOffset") {
                // Calls mobile feed toolbar to recalculate offset due to varying app pre header height
                const mobileFeedToolbarContainer = this.findComponent("mobile-feed-toolbar");
                if (mobileFeedToolbarContainer) {
                    mobileFeedToolbarContainer.updatePosition(event.offset);
                }
            }
        },

        onSearchOpen() {
            this.getLayoutContainer().classList.add("hidden");
            this.el.classList.add("lyst-app--full-height");
        },

        onSearchClose() {
            this.getLayoutContainer().classList.remove("hidden");
            this.el.classList.remove("lyst-app--full-height");
        },

        closeSaveForLaterCallout() {
            const header = this.findComponent(APP_HEADER_COMPONENT);

            if (!header) {
                return;
            }

            const saveForLaterComponent = header.findComponent("save-for-later-callout");
            if (saveForLaterComponent) {
                saveForLaterComponent.destroy();
            }
        },

        closeJoinCallout() {
            const header = this.findComponent(APP_HEADER_COMPONENT);

            if (!header) {
                return;
            }

            const joinCalloutComponent = header.findComponent("join-callout");

            if (joinCalloutComponent) {
                joinCalloutComponent.destroy();
            }
        },

        triggerSaveForLaterCallout() {
            if (!languageIsEnglish()) {
                return;
            }
            const header = this.findComponent(APP_HEADER_COMPONENT);

            if (!header) {
                return;
            }

            const saveForLaterComponent = header.findComponent("save-for-later-callout-prompt");

            if (saveForLaterComponent) {
                saveForLaterComponent.showSaveForLaterCallout();
            }
        },

        triggerJoinCallout() {
            const header = this.findComponent(APP_HEADER_COMPONENT);
            const isReadyToShow = analytics.getPageViewsCount() > 1 && !analytics.isReturningUser();
            const pageType = environment.get("pageType");
            const isDesktop = browser.getViewport() === browser.VIEWPORTS.DESKTOP;

            // Don't show if no header, is not the correct page view,
            // or user is on a sign in or sign up page
            if (
                !isDesktop ||
                !header ||
                !isReadyToShow ||
                this.membershipPages.indexOf(pageType) !== -1
            ) {
                return;
            }
        },

        onSearchLayoutInserted() {
            let backgroundBanner = this.findComponent("background-banner");
            let lystAppContainer = this.getLayoutContainer();

            if (backgroundBanner) {
                backgroundBanner.removeCoverImage();
            }

            if (lystAppContainer) {
                events
                    .onTransitionEnd(
                        this.el,
                        () => {
                            lystAppContainer.classList.add(LAYOUT_CONTAINER_ANIMATED_MODIFIER);
                            lystAppContainer.classList.remove(
                                LAYOUT_CONTAINER_COVER_IMAGE_MODIFIER
                            );
                        },
                        500,
                        "padding-top"
                    )
                    .then(() => {
                        lystAppContainer.classList.remove(LAYOUT_CONTAINER_ANIMATED_MODIFIER);
                    });
            }
        },

        closeCartSummary() {
            const header = this.findComponent(APP_HEADER_COMPONENT);

            if (header) {
                const cartSummary = header.findComponent("cart-summary");
                this.invoke(cartSummary, "close");
            }
        },

        addLayoutMatte() {
            const lystAppContainer = this.getLayoutContainer();

            if (lystAppContainer) {
                lystAppContainer.classList.add(LAYOUT_CONTAINER_DIMMED_MODIFIER);
            }

            this.closeSaveForLaterCallout();
        },

        removeLayoutMatte() {
            const lystAppContainer = this.getLayoutContainer();

            if (lystAppContainer) {
                lystAppContainer.classList.remove(LAYOUT_CONTAINER_DIMMED_MODIFIER);
            }

            this.unblockScrolling();
        },

        addLayoutMatteAndBlockScroll() {
            this.addLayoutMatte();
            this.blockScrolling();
        },

        _onOverlayOpen(event) {
            this.overlayStack.push(event.target);
            this.blockScrolling();
        },

        _onOverlayCloseIntent(event) {
            const activeOverlay = this.overlayStack.peek();

            // If the event is not coming from the active
            // overlay, preventDefault and do nothing.
            if (activeOverlay !== event.target) {
                event.preventDefault();
                return;
            }
        },

        _onOverlayClose() {
            const header = this.findComponent("app-header");

            this.overlayStack.pop();

            // If there is no active overlay,
            // unblock the scrolling
            if (this.overlayStack.isEmpty()) {
                if (header && header.openCallouts) {
                    return;
                }

                this.unblockScrolling();
                this.unFixPageContent();
            }
        },

        closeSaveForLaterCallout() {
            const callout = this.findComponent("save-for-later-callout");
            if (callout) {
                callout.destroy();
            }
        },

        /**
         * On every save for later button insert event, pushes the product ID
         * into a list. Then fetches the pins and re-render the SFL button
         * if it has to.
         *
         * @param {Event} event
         */
        _onSaveForLaterButtonInserted(event) {
            this.sflProductIdsQueue.push(event.productId);
            this._fetchSaveForLaterPins();
        },

        /**
         * Requests the pins API endpoint passing the list of product ids in the
         * page. That will return an object with a map of the ids to their pin
         * status, i.e.
         *      {
         *          00000: True,  # The user has saved/pinned this product...
         *          00101: False, # ... But not this one.
         *          01010: True   # etc.
         *      }
         * Runs every 50ms – debounced.
         */
        _fetchSaveForLaterPins: _.debounce(function() {
            const endpoint = "/services/pins/";
            const query = url.toURLSearchParams({
                product_id: [...this.sflProductIdsQueue]
            });

            this.sflProductIdsQueue = [];

            requester
                .get(endpoint, query)
                .then(data => {
                    this._setPinnedProducts(data.pin_mapping);
                })
                .catch(error => logging.error(error));
        }, 50),

        /**
         * Loops through the save for later buttons in the page and re-renders them
         * based on the user-saved product pins.
         *
         * @param {Object} pinnedProductsMapping - The product id mapping
         */
        _setPinnedProducts(pinnedProductsMapping) {
            const sflButtons = this.findComponents("save-for-later-button");
            sflButtons.forEach(saveForLaterButton => {
                const isPinned = pinnedProductsMapping[saveForLaterButton.options.productId];
                if (isPinned) {
                    saveForLaterButton._render(true);
                }
            });
        },

        /**
         * Displays a sign up overlay, with the set properties. This is fired when the
         * `triggerSignup` event is fired.
         *
         * The options passed in are expected from the event that is fired. If no parameters
         * are passed, then the defaults as defined in the SignupOverlay are used instead.
         *
         * @param {string} [path]: the path of the registration form to load
         * @param {object} [queryParams]: an key/value pairing of the query parameters to pass
         * @param {string} [formType]: the type of form to show (ie. `signup`)
         * @param {string} [pageType]: the page type to set the environment to
         * @param {string} [pageSubType]: the page subtype to set the environment to
         */
        showSignUpOverlay({ path, queryParams, pageType, pageSubType, formType }) {
            // Redirect back to the current page if login successful (this might not always be true,
            // as we might decide where to redirect in the backend)
            const loginSuccessRedirectUrl = globals.window.location.href;

            // Set history to signup or login page
            // To be handled by the router
            history.pushState(path, queryParams);

            // Set environment properties so that we can use them in
            // the signup-overlay constructor or while on the page
            environment.set({
                formType,
                pageType,
                pageSubType,
                loginSuccessRedirectUrl
            });
        },

        showCsaUnit(event) {
            const position = event["position"];
            const csaContainers = this.findComponents("adsense-custom-search-ads");
            csaContainers.forEach(csaContainer => {
                if (csaContainer.options.dataPosition == position) {
                    csaContainer.createCsaUnit();
                }
            });
        },

        onLayoutMatteClick() {
            this.removeLayoutMatte();

            const savedSearchCreatorComponent = this.findComponent("saved-search-creator");
            if (savedSearchCreatorComponent) {
                savedSearchCreatorComponent.closeDropDownsAndBlurElements();
            }
        },

        showInstantFeedback({ area }) {
            this.findComponents("instant-feedback").forEach(component => {
                if (component.options.area === area) {
                    component.show();
                }
            });
        },

        attachWatcher() {
            const GET_TRIGGER_SIGNUP = gql`
                query {
                    triggerSignup @client {
                        clickCount
                    }
                }
            `;

            client
                .watchQuery({ query: GET_TRIGGER_SIGNUP, fetchResults: false })
                .subscribe(response => {
                    if (response.data.triggerSignup.clickCount !== 0) {
                        this.emit("triggerSignup", {
                            path: "/account/register/",
                            queryParams: {
                                next: [
                                    globals.window.location.pathname +
                                        globals.window.location.search
                                ],
                                type: ["save_for_later"]
                            },
                            formType: "save_for_later_signup",
                            pageType: "save_for_later",
                            pageSubType: "signup_or_login"
                        });
                    }
                });
        }
    }
);

